<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none car-locations-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in carLocations"
        :key="item._id"
        class="car-locations-mobile-card"
        :class="{
          'car-locations-mobile-card--selected': index === activeRow,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <div class="car-locations-mobile-card__main">
          <div class="car-locations-mobile-card__media">
            <button
              type="button"
              class="car-locations-mobile-card__qr-image-btn"
              @click.stop="
                openQrDialog({
                  id: item._id,
                  type: 'carLocations',
                  printed: item.printed,
                })
              "
            >
              <qr-code
                v-if="item._id"
                class="car-locations-mobile-card__qr"
                :size="60"
                :text="item._id"
              />
            </button>
            <button
              type="button"
              class="car-locations-mobile-card__qr-value-btn"
              @click.stop="
                openQrDialog({
                  id: item._id,
                  type: 'carLocations',
                  printed: item.printed,
                })
              "
            >
              {{ getCarLocationQrId(item) }}
            </button>
          </div>

          <div class="car-locations-mobile-card__info">
            <div class="car-locations-mobile-card__info-top">
              <div class="font-weight-bold text-primary text-body-2">
                {{ item.name }}
              </div>
              <div v-if="item.description" class="text-caption text-grey-darken-1 mt-1">
                {{ item.description }}
              </div>
              <div
                v-if="
                  ($store.getters.role == 'LekoAdmin' ||
                    $store.getters.role == 'LekoEmployee' ||
                    $store.getters.role == 'SuperAdmin') &&
                  item.institution_id
                "
                class="text-caption mt-2"
              >
                <v-chip size="small">
                  {{ item.institution_id.title }}
                </v-chip>
              </div>
            </div>

            <v-btn
              v-if="item.date_created"
              color="black"
              variant="outlined"
              size="x-small"
              class="car-locations-mobile-card__date mt-2"
              readonly
              @click.stop
            >
              {{ formatDate(item.date_created) }}
            </v-btn>
          </div>
        </div>

        <div class="car-locations-mobile-card__actions">
          <v-spacer />
          <v-btn icon variant="text" size="small" @click.stop="viewCarsList(item._id)">
            <v-icon size="20" color="grey-darken-2">
              mdi-format-list-bulleted
            </v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="editCarLocation(item)">
            <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="deleteCarLocation(item._id)">
            <v-icon size="20" color="grey-darken-2">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert v-if="!loading && carLocations.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="car-locations-table-host pb-2">
      <v-data-table-server
        v-model:options="pagination"
        :headers="headers"
        :items="carLocations"
        :items-length="totalItems"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        item-value="_id"
        return-object
        :loading="loading"
        class="elevation-1 nowrap-headers"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index == activeRow }"
            class="tableRowColor"
            @click="activeRow = activeRow === index ? null : index"
          >
            <!--QR CODE-->
            <td>
              <v-row
                class="pointer ma-1 text-center justify-center d-flex flex-column"
                @click="
                  openQrDialog({
                    id: item._id,
                    type: 'carLocations',
                    printed: item.printed,
                  })
                "
              >
                <qr-code
                  class="justify-center d-flex"
                  :size="60"
                  :text="item._id"
                />
                <h4>
                  {{ item._id.slice(item._id.length - 6, item._id.length) }}
                </h4>
              </v-row>
            </td>
            <!--NAME-->
            <td class="text-xs">
              {{ item.name }}
            </td>
            <!--DESCRIPTION-->
            <td class="text-xs">
              {{ item.description }}
            </td>
            <!--DESCRIPTION-->
            <td
              v-if="
                $store.getters.role == 'LekoAdmin' ||
                $store.getters.role == 'LekoEmployee' ||
                $store.getters.role == 'SuperAdmin'
              "
              class="text-xs"
            >
              <span v-if="item.institution_id">
                <v-chip>
                  {{ item.institution_id.title }}
                </v-chip>
              </span>
            </td>
            <!--DATE-->
            <td class="text-xs">
              <v-btn variant="outlined">
                {{ formatDate(item.date_created) }}
              </v-btn>
            </td>
            <!--ACTIONS-->
            <td class="car-location-actions-cell">
              <div class="car-location-actions d-flex flex-nowrap align-center">
                <!--View cars list-->
                <v-btn
                  icon
                  variant="text"
                  class="flex-shrink-0"
                  @click="viewCarsList(item._id)"
                >
                  <v-icon color="grey-darken-2">
                    mdi-format-list-bulleted
                  </v-icon>
                </v-btn>
                <!--Edit location-->
                <v-btn
                  icon
                  variant="text"
                  class="flex-shrink-0"
                  @click="editCarLocation(item)"
                >
                  <v-icon color="grey-darken-2"> mdi-pencil </v-icon>
                </v-btn>
                <!--Delete location-->
                <v-btn
                  icon
                  variant="text"
                  class="flex-shrink-0"
                  @click="deleteCarLocation(item._id)"
                >
                  <v-icon color="grey-darken-2"> mdi-delete </v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table-server>
    </div>
    <v-dialog
      v-model="dialogDelete"
      width="520"
    >
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0 car-location-delete-alert"
            :model-value="true"
            type="error"
            variant="tonal"
            border="bottom"
          >
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer class="d-none d-sm-flex" />

          <!-- Mobile: stacked full-width buttons -->
          <div class="d-flex d-sm-none flex-column ga-2 w-100 px-4 pb-4">
            <v-btn variant="outlined" block @click="dialogDelete = false">
              {{ $t("close") }}
            </v-btn>
            <v-btn color="red" variant="outlined" block @click="confirmDelete()">
              {{ $t("delete") }}
            </v-btn>
          </div>

          <!-- Desktop: inline buttons -->
          <div class="d-none d-sm-flex">
            <v-btn variant="text" @click="dialogDelete = false">
              {{ $t("close") }}
            </v-btn>
            <v-btn color="red" variant="outlined" class="ml-2" @click="confirmDelete()">
              {{ $t("delete") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";

export default {
  components: {
    "qr-code": VueQrCode,
  },
  props: ["search"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    headers: [
      {
        title: "Qr Code",
        sortable: false,
        key: "old_id",
      },
      {
        title: "Name",
        sortable: true,
        key: "name",
      },
      {
        title: "Description",
        sortable: true,
        key: "description",
      },
      // Insert institution here.
      {
        title: "Date",
        sortable: true,
        key: "date_created",
      },
      {
        title: "Actions",
        sortable: false,
        key: "",
      },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["_id"],
      sortBy: { key: "_id", order: "desc" },
      sortDesc: [true],
    },
    carLocations: [],
    totalItems: 0,
    loading: false,
    activeRow: null,
    dialogDelete: false,
    carLocationToDelete: null,
  }),
  computed: {
    searchField() {
      return this.$props.search;
    },
  },
  watch: {
    pagination: {
      handler() {
        if (!this.searchField || this.searchField.length < 1)
          this.getCarLocations();
        else this.searchCarLocations(this.searchField);
      },
    },
    searchField: {
      handler(value) {
        if (!value || value.length < 1) this.getCarLocations();
        else this.searchCarLocations(value);
      },
      deep: true,
    },
  },
  created() {
    this.getLocale();
    this.getCarLocations();
    this.activeRow = null;
  },
  methods: {
    getCarLocationQrId(item) {
      if (!item?._id) return "";
      return item._id.slice(item._id.length - 6, item._id.length);
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("D MMMM YYYY");
    },
    getLocale() {
      this.headers[0].title = this.$t("car_locations.table.qr_code");
      this.headers[1].title = this.$t("car_locations.table.name");
      this.headers[2].title = this.$t("car_locations.table.description");
      this.headers[3].title = this.$t("car_locations.table.date");
      this.headers[4].title = this.$t("car_locations.table.actions");

      // Insert institution header.
      if (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "LekoEmployee" ||
        this.$store.getters.role === "SuperAdmin"
      ) {
        this.headers.splice(3, 0, {
          title: "Institution",
          sortable: false,
          key: "institution_id",
        });
        this.headers[3].title = this.$t("car_locations.table.institution");
        // Overwrite headers' values.
        this.headers[4].title = this.$t("car_locations.table.date");
        this.headers[5].title = this.$t("car_locations.table.actions");
      }
    },
    getCarLocations() {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "_id",
        order: "desc",
      };
      getRequest(
        "/car-location",
        `role=${this.$store.getters.role}&institution_id=${
          this.$store.getters.institutionId
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.carLocations = response.carLocations;
          this.totalItems = response.count;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    searchCarLocations(value) {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "_id",
        order: "desc",
      };
      getRequest(
        "/car-location",
        `role=${this.$store.getters.role}&search=${value}&institution_id=${
          this.$store.getters.institutionId
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.carLocations = response.carLocations;
          this.totalItems = response.count;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    viewCarsList(id) {
      this.$emit("cars-list", id);
    },
    editCarLocation(carLocation) {
      this.$emit("edit", carLocation);
    },
    deleteCarLocation(id) {
      this.carLocationToDelete = id;
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loading = true;
      deleteRequest("/car-location", `id=${this.carLocationToDelete}`)
        .then(() => {
          this.getCarLocations();
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.dialogDelete = false;
          this.carLocationToDelete = null;
        });
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length ? options.groupBy : ["_id"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "_id", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .car-locations-table-host :deep(.v-table__wrapper),
  .car-locations-table-host :deep(.v-data-table__wrapper),
  .car-locations-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .car-locations-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .car-locations-mobile-list {
    display: none !important;
  }
}

.car-locations-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.car-locations-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.car-locations-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px 10px 6px;
  gap: 10px;
}
.car-locations-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.car-locations-mobile-card__qr-image-btn {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.car-locations-mobile-card__qr {
  display: flex;
  justify-content: center;
}
.car-locations-mobile-card__qr-value-btn {
  min-width: 72px;
  margin-top: 6px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.car-locations-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.car-locations-mobile-card__info-top {
  padding-right: 0;
}
.car-locations-mobile-card__date {
  height: auto !important;
  min-height: 0 !important;
  text-transform: none;
  letter-spacing: normal;
}
.car-locations-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 6px;
}

.car-location-actions-cell {
  white-space: nowrap;
}

.car-location-actions {
  flex-wrap: nowrap;
}

.car-location-delete-alert :deep(.v-alert__content) {
  white-space: normal;
}
</style>
